// Accessibility statement — UK Equality Act 2010, the European Accessibility Act and WCAG 2.2 AA.
import React, { useEffect } from "react";
import { openAccessibility } from "@/components/a11y/CommandPalette";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-xl font-bold tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-7 text-neutral-700 dark:text-neutral-300">{children}</div>
  </section>
);

export default function AccessibilityPage() {
  useEffect(() => { document.title = "Accessibility · Fable+"; }, []);
  return (
    <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="accessibility-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Fable+</p>
      <h1 className="mt-2 text-4xl font-extrabold tracking-tight">Accessibility statement</h1>
      <p className="mt-4 text-lg text-neutral-700 dark:text-neutral-300">
        Fable+ should work for everyone. We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.2 at level AA across the website and the app,
        in line with the UK Equality Act 2010 and the European Accessibility Act.
      </p>
      <button type="button" onClick={openAccessibility} className="mt-6 h-12 rounded-full bg-neutral-900 px-6 text-sm font-bold text-white dark:bg-white dark:text-neutral-900" data-testid="open-a11y-settings">
        Open accessibility settings
      </button>

      <Section title="What you can do">
        <ul className="list-disc space-y-2 pl-5">
          <li>Make text up to 150% larger without breaking the page, and turn on extra line and letter spacing.</li>
          <li>Switch on high contrast, underlined links and a strong keyboard focus outline.</li>
          <li>Reduce motion. We also follow your device's "reduce motion" setting automatically.</li>
          <li>Use a font designed for low vision (Atkinson Hyperlegible).</li>
          <li>Get around with the keyboard alone: Tab through every control, press Ctrl / ⌘ + K to jump to any page, ? for shortcuts, and use "Skip to main content".</li>
          <li>Use a screen reader: controls carry labels, images carry descriptions and dialogs trap and return focus.</li>
          <li>In the app: system text size, Dark / Light / Sepia and other themes all tuned to at least 4.5 : 1 contrast for body text, VoiceOver and TalkBack labels, and captions on video.</li>
        </ul>
      </Section>

      <Section title="Known limitations">
        <ul className="list-disc space-y-2 pl-5">
          <li>Some creator videos uploaded before captions were automatic may not have captions yet.</li>
          <li>Some partner content (maps, flight and hotel results, embedded players) comes from third parties and may not fully meet WCAG 2.2 AA.</li>
          <li>Some older PDF documents uploaded by businesses may not be tagged for screen readers.</li>
        </ul>
        <p>We review the platform regularly and fix issues as we find them.</p>
      </Section>

      <Section title="Tell us about a problem">
        <p>
          If something is hard to use, or you need information in a different format, email{" "}
          <a className="font-semibold underline" href="mailto:hello@fableplus.uk?subject=Accessibility">hello@fableplus.uk</a> with the page and what happened.
          We aim to reply within 5 working days.
        </p>
        <p>
          If you're not happy with our response, in the UK you can contact the Equality Advisory and Support Service (EASS). In the EU you can contact the
          market surveillance authority in your country.
        </p>
      </Section>

      <p className="mt-12 text-sm text-neutral-500">This statement was last reviewed on 31 October 2026.</p>
    </main>
  );
}
